<!-- Hero Section with Background Image -->
<div class="stores-hero">
  <div class="stores-hero-bg"></div>
  <div class="stores-hero-overlay"></div>
  <div class="container-xxl py-10 position-relative">
    <!-- Page Header -->
    <div class="d-flex flex-wrap flex-stack mb-10">
      <h1 class="fw-bold my-2 text-white"><i class="fa fa-store me-3" style="color: #27ae60;"></i>{{ 'storesServices.title' | translate }}</h1>
    </div>

    <!-- Filters Section -->
    <div class="card mb-0 shadow-lg">
      <div class="card-body">
        <div class="row g-5 align-items-end">
          <!-- Search Filter -->
          <div class="col-md-5">
            <app-input-component
              [type]="'text'"
              [(value)]="searchQuery"
              [label]="'storesServices.search' | translate"
              [placeholder]="'storesServices.searchPlaceholder' | translate"
              [floating]="true"
              (valueChange)="onSearchChange()"
            />
          </div>

          <!-- Category Filter -->
          <div class="col-md-3">
            <app-selectorini
              [options]="categoryOptions"
              [displayKey]="'name'"
              [valueKey]="'id'"
              [(value)]="selectedCategory"
              [label]="'storesServices.category' | translate"
              [placeholder]="'storesServices.allCategories' | translate"
              [floating]="true"
              (valueChange)="onCategoryChange()"
            />
          </div>

          <!-- Sort by Nearest Button -->
          <div class="col-md-2">
            <button
              class="btn w-100"
              [class.btn-primary]="sortByDistance"
              [class.btn-light]="!sortByDistance"
              (click)="toggleSortByDistance()"
              [disabled]="!userLocation"
              style="height: 50px;"
              [title]="userLocation ? ('competitions.sortByNearest' | translate) : ('competitions.waitingForLocation' | translate)"
            >
              @if (locationLoading) {
                <span class="spinner-border spinner-border-sm me-2" role="status"></span>
              } @else {
                <i class="fa fa-location-crosshairs me-2"></i>
              }
              {{ 'competitions.nearest' | translate }}
            </button>
          </div>

          <!-- Clear Filters Button -->
          <div class="col-md-2">
            <app-button
              [variant]="'neutral'"
              [iconClass]="'fa fa-times'"
              [label]="'competitions.clear' | translate"
              [width]="'100%'"
              (clicked)="clearFilters()"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<div class="container-xxl py-10">

  <!-- Hot Deals Section -->
  @if (!loadingDeals && activeDeals.length > 0) {
    <div class="card mb-10">
      <div class="card-header">
        <h3 class="card-title fw-bold">
          <i class="fa fa-fire text-danger me-2"></i>
          {{ 'storesServices.hotDeals' | translate }}
        </h3>
      </div>
      <div class="card-body">
        <div class="row g-6 g-xl-9">
          @for (deal of activeDeals; track deal.deal_id) {
            <div class="col-md-6 col-lg-4 col-xl-3">
              <app-deal-card
                [deal]="deal"
                [showCompanyInfo]="true"
              />
            </div>
          }
        </div>
      </div>
    </div>
  }

  <!-- Map Section -->
  @if (!loading && filteredCompanies.length > 0) {
    <div class="mb-10">
      <app-location-map
        [markers]="mapMarkers"
        [config]="mapConfig"
        [loading]="geocodingInProgress"
        (markerClick)="onMapMarkerClick($event)"
        (infoWindowAction)="onMapInfoWindowAction($event)"
      />
      <!-- Map Legend - Categories -->
      @if (categories.length > 0) {
        <div class="d-flex flex-wrap gap-4 mt-4 justify-content-center">
          @for (category of categories; track category.category_id) {
            <div class="d-flex align-items-center">
              @if (category.icon) {
                <i class="fa {{ category.icon }} me-2 text-primary"></i>
              }
              <span class="text-muted fs-7">{{ translateCategory(category) }}</span>
            </div>
          }
        </div>
      }
    </div>
  }

  <!-- Loading State -->
  @if (loading) {
    <div class="d-flex flex-column align-items-center py-20">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ 'storesServices.loading' | translate }}</span>
      </div>
      <p class="text-muted mt-5">{{ 'storesServices.loading' | translate }}</p>
    </div>
  }

  <!-- No Results -->
  @if (!loading && filteredCompanies.length === 0) {
    <div class="card">
      <div class="card-body d-flex flex-column align-items-center py-20">
        <i class="fa fa-store fs-3x text-muted mb-5"></i>
        <h3 class="text-muted">{{ 'storesServices.noResults' | translate }}</h3>
        <p class="text-muted">{{ 'storesServices.noResultsHint' | translate }}</p>
      </div>
    </div>
  }

  <!-- Companies Grid -->
  @if (!loading && filteredCompanies.length > 0) {
    <div class="row g-6 g-xl-9">
      @for (company of filteredCompanies; track company.company_id) {
        <div class="col-md-6 col-lg-4">
          <div class="card h-100 store-card" [id]="'store-card-' + company.company_id">
            <!-- Header Image -->
            <div class="store-header">
              @if (company.header_image_url) {
                <img [src]="company.header_image_url" class="header-image" [alt]="company.company_name"/>
              } @else {
                <div class="header-placeholder">
                  <i class="fa fa-store"></i>
                </div>
              }
            </div>

            <div class="card-body d-flex flex-column">
              <!-- Category & Distance Badges -->
              <div class="d-flex flex-wrap gap-2 mb-3">
                @if (company.category) {
                  <span class="category-badge">
                    @if (company.category.icon) {
                      <i class="fa {{ company.category.icon }}"></i>
                    }
                    {{ translateCategory(company.category) }}
                  </span>
                }
                @if (userLocation && getCompanyDistance(company) !== null) {
                  <span class="badge badge-primary">
                    <i class="fa fa-location-arrow me-1"></i>
                    {{ formatDistance(getCompanyDistance(company)) }} {{ 'organisations.away' | translate }}
                  </span>
                }
                @if (hasActiveDeal(company)) {
                  <span class="badge bg-success text-white">
                    <i class="fa fa-tag me-1"></i>
                    {{ 'storesServices.dealBadge' | translate }}
                  </span>
                }
              </div>

              <!-- Logo + Name Row -->
              <div class="d-flex align-items-center mb-4">
                <div class="store-logo me-3">
                  @if (company.company_logo_url) {
                    <img [src]="company.company_logo_url" [alt]="company.company_name"/>
                  } @else {
                    <div class="logo-placeholder">
                      <span>{{ getCompanyInitials(company) }}</span>
                    </div>
                  }
                </div>
                <div>
                  <h3 class="card-title fw-bold fs-4 mb-1">{{ company.company_name }}</h3>
                  @if (company.city || company.country) {
                    <p class="text-muted mb-0 fs-7">
                      <i class="fa fa-location-dot me-1"></i>
                      {{ company.city }}{{ company.city && company.country ? ', ' : '' }}{{ company.country }}
                    </p>
                  }
                </div>
              </div>

              <!-- Description (HTML formatted) -->
              @if (company.description) {
                <div class="text-gray-600 mb-5 store-description" [innerHTML]="company.description"></div>
              }

              <!-- Contact Info -->
              <div class="mb-5 mt-auto">
                @if (company.email) {
                  <div class="d-flex align-items-center mb-2">
                    <i class="fa fa-envelope text-primary me-3 fs-6"></i>
                    <a [href]="'mailto:' + company.email" class="text-gray-700 text-hover-primary text-truncate">{{ company.email }}</a>
                  </div>
                }
                @if (company.phone) {
                  <div class="d-flex align-items-center">
                    <i class="fa fa-phone text-primary me-3 fs-6"></i>
                    <a [href]="'tel:' + company.phone" class="text-gray-700 text-hover-primary">{{ company.phone }}</a>
                  </div>
                }
              </div>

              <!-- Action Button -->
              <div class="mt-auto">
                <app-button
                  [variant]="'primary'"
                  [label]="'storesServices.viewDetails' | translate"
                  [routerLink]="'/stores-services/' + company.company_id"
                  [width]="'100%'"
                />
              </div>
            </div>
          </div>
        </div>
      }
    </div>
  }
</div>
